<section class="upload" [class.disabled]="disabled()">
  <div
    class="dropzone"
    [class.is-dragover]="isDragover()"
    [tabindex]="disabled() ? -1 : 0"
    role="button"
    [attr.aria-disabled]="disabled()"
    (click)="picker.click()"
    (keydown.enter)="picker.click()"
    (keydown.space)="$event.preventDefault(); picker.click()"
    (dragover)="handleDragOver($event)"
    (dragleave)="handleDragLeave($event)"
    (drop)="handleDrop($event)"
  >
    <div class="dropzone-title">{{ title() | translate }}</div>
    <div class="dropzone-subtitle">{{ subTitle() | translate }}</div>
    @if (allowedFileTypes().length) {
      <div class="dropzone-hint">
        {{ 'uploader.dropzone.allowed' | translate: { types: allowedFileTypes().join(', ') } }}
      </div>
    }
    @if (forbiddenFileTypes().length) {
      <div class="dropzone-hint">
        {{ 'uploader.dropzone.forbidden' | translate: { types: forbiddenFileTypes().join(', ') } }}
      </div>
    }
    @if (maxUploadSize() !== undefined) {
      <div class="dropzone-hint">
        {{ 'uploader.dropzone.maxTotal' | translate: { size: formatSize(maxUploadSize()!) } }}
      </div>
    }
    @if (maxFiles() !== undefined) {
      <div class="dropzone-hint">
        {{ 'uploader.dropzone.maxFiles' | translate: { count: maxFiles() } }}
      </div>
    }
    <input
      #picker
      type="file"
      multiple
      [accept]="acceptAttr()"
      [disabled]="disabled()"
      (change)="handleInput($event)"
      hidden
    />
  </div>

  @if (maxUploadSize() !== undefined) {
    <div class="quota" [class.exceeded]="exceedsLimit()">
      @if (exceedsLimit()) {
        {{
          'uploader.quota.sizeExceeded'
            | translate
              : { over: formatSize(-remainingSize()!), limit: formatSize(maxUploadSize()!) }
        }}
      } @else {
        {{
          'uploader.quota.sizeAvailable'
            | translate
              : { remaining: formatSize(remainingSize()!), limit: formatSize(maxUploadSize()!) }
        }}
      }
    </div>
  }

  @if (maxFiles() !== undefined) {
    <div class="quota" [class.exceeded]="exceedsFileCount()">
      @if (exceedsFileCount()) {
        {{ 'uploader.quota.filesExceeded' | translate: { count: fileCount(), limit: maxFiles() } }}
      } @else {
        {{ 'uploader.quota.filesCount' | translate: { count: fileCount(), limit: maxFiles() } }}
      }
    </div>
  }

  @if (errors().length) {
    <ul class="messages">
      @for (msg of errors(); track msg) {
        <li class="messages-item">{{ msg }}</li>
      }
    </ul>
  }

  @if (hasFiles()) {
    <ul class="files">
      @for (
        file of files();
        track file.name + ':' + file.size + ':' + file.lastModified;
        let i = $index
      ) {
        @let state = stateFor(file);
        <li
          class="files-item"
          [class.is-uploading]="state?.status === 'uploading'"
          [class.is-success]="state?.status === 'success'"
          [class.is-error]="state?.status === 'error'"
        >
          <span class="files-name">{{ file.name }}</span>
          <span class="files-size">{{ formatSize(file.size) }}</span>
          <span class="files-status">
            @switch (state?.status) {
              @case ('uploading') {
                <span
                  class="files-progress"
                  role="progressbar"
                  [attr.aria-valuenow]="state!.progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <span class="files-progress-bar" [style.width.%]="state!.progress"></span>
                </span>
                <span class="files-progress-label">{{ state!.progress }} %</span>
              }
              @case ('success') {
                <span
                  class="files-badge files-badge--success"
                  [attr.aria-label]="'uploader.file.uploadSuccess' | translate"
                  >✓</span
                >
              }
              @case ('error') {
                <span
                  class="files-badge files-badge--error"
                  [attr.aria-label]="state?.message ?? ('uploader.file.uploadFailed' | translate)"
                  [attr.title]="state?.message ?? ('uploader.file.uploadFailed' | translate)"
                  >✗</span
                >
              }
              @default {
                <button
                  type="button"
                  class="files-remove"
                  [disabled]="disabled()"
                  (click)="removeFile(i)"
                  [attr.aria-label]="'uploader.file.remove' | translate: { name: file.name }"
                >
                  ×
                </button>
              }
            }
          </span>
          @if (state?.status === 'error' && state?.message) {
            <span class="files-message">{{ state!.message }}</span>
          }
        </li>
      }
    </ul>
  }

  @if (!isCompleted()) {
    <div class="actions">
      <claas-button
        [text]="'uploader.actions.clear' | translate"
        buttonClass="secondaryOutline"
        [disabled]="!hasFiles() || disabled()"
        (onClick)="clear()"
      ></claas-button>
      <claas-button
        [text]="
          (hasFiles() ? 'uploader.actions.uploadCount' : 'uploader.actions.upload')
            | translate: { count: fileCount() }
        "
        buttonClass="primary"
        [disabled]="!canUpload()"
        (onClick)="submit()"
      ></claas-button>
    </div>
  }
</section>
